import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, map } from 'rxjs';

import { getContrastTextColor } from '../../../ui/forms/color-picker';
import { WidgetConfiguration } from '../../models';
import { WidgetComponent } from '../widget.component';

const DEFAULT_ACCENT = '#3730a3';

@Component({
  selector: 'rms-widget-fab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [AsyncPipe, MatIconModule, WidgetComponent],
  template: `
    @let bgColor = fabBgColor$ | async;
    @let textColor = fabTextColor$ | async;

    <button
      #fabBtn
      class="rms-widget-fab-btn"
      type="button"
      [style.background-color]="bgColor"
      [style.color]="textColor"
      (click)="togglePanel()">
      <mat-icon svgIcon="remix:Device/phone-line" />
    </button>

    <ng-template #panelTpl>
      <div class="rms-widget-fab-panel">
        <rms-widget
          [config]="config$ | async"
          [lang]="lang"
          [previewMode]="previewMode" />
      </div>
    </ng-template>
  `,
  styles: `
    .rms-widget-fab-btn {
      width: 3.5rem;
      height: 3.5rem;
      border-radius: 50%;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;

      mat-icon {
        font-size: 1.5rem;
        width: 1.5rem;
        height: 1.5rem;
      }

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
      }
    }

    .rms-widget-fab-panel {
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
    }
  `,
})
export class WidgetFabComponent implements OnDestroy {
  @Input() public set config(val: WidgetConfiguration | null) {
    this.config$.next(val);
  }

  @Input() public lang: 'en' | 'it' = 'en';
  @Input() public previewMode = false;

  protected readonly config$ = new BehaviorSubject<WidgetConfiguration | null>(null);

  protected readonly fabBgColor$ = this.config$.pipe(map((c) => c?.styles?.accentColor ?? DEFAULT_ACCENT));

  protected readonly fabTextColor$ = this.config$.pipe(
    map((c) => getContrastTextColor(c?.styles?.accentColor ?? DEFAULT_ACCENT))
  );

  @ViewChild('fabBtn') private fabBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelTpl') private panelTplRef?: TemplateRef<void>;

  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private panelOverlayRef: OverlayRef | null = null;

  protected togglePanel(): void {
    if (this.panelOverlayRef?.hasAttached()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  private openPanel(): void {
    const fabBtn = this.fabBtnRef;
    const panelTpl = this.panelTplRef;
    if (!fabBtn || !panelTpl) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(fabBtn)
      .withPositions([
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -16 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 16 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -16 },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 16 },
      ])
      .withFlexibleDimensions(false)
      .withLockedPosition(true)
      .withPush(true)
      .withViewportMargin(8);

    this.panelOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.panelOverlayRef.backdropClick().subscribe(() => this.closePanel());
    this.panelOverlayRef.attach(new TemplatePortal(panelTpl, this.vcr));
  }

  private closePanel(): void {
    this.panelOverlayRef?.dispose();
    this.panelOverlayRef = null;
  }

  ngOnDestroy(): void {
    this.closePanel();
  }
}
